import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import api from '../lib/api';

const THEME_STORAGE_KEY = 'fieldcert-theme';
const LEGACY_THEME_KEYS = ['kadence-theme', 'sitelogic-theme'];
const DENSITY_STORAGE_KEY = 'fieldcert-density';
const DENSITY_VALUES = ['comfortable', 'compact'];

const ThemeContext = createContext(null);

function getStoredDensity() {
  if (typeof localStorage === 'undefined') return null;
  const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
  return DENSITY_VALUES.includes(stored) ? stored : null;
}

function applyDensityToDocument(density) {
  document.documentElement.setAttribute('data-density', density);
}

function getStoredPreference() {
  let stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (!stored && typeof localStorage !== 'undefined') {
    for (const k of LEGACY_THEME_KEYS) {
      const legacy = localStorage.getItem(k);
      if (!legacy) continue;
      localStorage.setItem(THEME_STORAGE_KEY, legacy);
      localStorage.removeItem(k);
      stored = legacy;
      break;
    }
  }
  return stored;
}

function resolveEffectiveTheme(preference) {
  if (preference === 'light') return 'light';
  if (preference === 'dark') return 'dark';
  if (preference === 'system') {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }
  return 'dark';
}

function applyThemeToDocument(effective) {
  document.documentElement.setAttribute('data-theme', effective);
}

export function ThemeProvider({ children }) {
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);
  const [effectiveTheme, setEffectiveTheme] = useState(() => {
    const stored = getStoredPreference();
    const pref = user?.themePreference || stored || 'dark';
    return resolveEffectiveTheme(pref);
  });

  const preference = user?.themePreference || getStoredPreference() || 'dark';
  const densityPreference = user?.densityPreference || getStoredDensity() || 'comfortable';

  useEffect(() => {
    const next = resolveEffectiveTheme(preference);
    setEffectiveTheme(next);
    applyThemeToDocument(next);
    if (preference) localStorage.setItem(THEME_STORAGE_KEY, preference);
  }, [preference]);

  useEffect(() => {
    applyDensityToDocument(densityPreference);
    localStorage.setItem(DENSITY_STORAGE_KEY, densityPreference);
  }, [densityPreference]);

  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => {
      if (preference === 'system') {
        const next = mq.matches ? 'light' : 'dark';
        setEffectiveTheme(next);
        applyThemeToDocument(next);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [preference]);

  const setThemePreference = useCallback(
    async (newPreference) => {
      const allowed = ['dark', 'light', 'system'];
      if (!allowed.includes(newPreference)) return;
      const userId = user?.id ?? user?.userId;
      if (!userId) {
        localStorage.setItem(THEME_STORAGE_KEY, newPreference);
        const next = resolveEffectiveTheme(newPreference);
        setEffectiveTheme(next);
        applyThemeToDocument(next);
        return;
      }
      try {
        await api.put(`/users/${userId}/profile`, { themePreference: newPreference });
        updateUser({ themePreference: newPreference });
        localStorage.setItem(THEME_STORAGE_KEY, newPreference);
        const next = resolveEffectiveTheme(newPreference);
        setEffectiveTheme(next);
        applyThemeToDocument(next);
      } catch (err) {
        localStorage.setItem(THEME_STORAGE_KEY, newPreference);
        const next = resolveEffectiveTheme(newPreference);
        setEffectiveTheme(next);
        applyThemeToDocument(next);
      }
    },
    [user?.id, user?.userId, updateUser]
  );

  const setDensityPreference = useCallback(
    async (newDensity) => {
      if (!DENSITY_VALUES.includes(newDensity)) return;
      localStorage.setItem(DENSITY_STORAGE_KEY, newDensity);
      applyDensityToDocument(newDensity);
      const userId = user?.id ?? user?.userId;
      if (!userId) return;
      try {
        await api.put(`/users/${userId}/profile`, { densityPreference: newDensity });
        updateUser({ densityPreference: newDensity });
      } catch {
        // Local preference already applied; ignore transient API failure.
      }
    },
    [user?.id, user?.userId, updateUser]
  );

  const value = useMemo(
    () => ({
      themePreference: preference,
      effectiveTheme,
      setThemePreference,
      densityPreference,
      setDensityPreference,
    }),
    [preference, effectiveTheme, setThemePreference, densityPreference, setDensityPreference]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
